import { W, H, GROUND, DOOR_W, DOOR_H, PLATFORMS, DOORS } from "./world";
import { renderEscape } from "./escape";
import { drawVents, drawPuffs } from "./gas";

const C = {
  bg: "#0b0c10",
  brick: "rgba(200,190,170,0.028)",
  plat: "#171a22",
  platEdge: "#2b303d",
  frame: "#1d2029",
  frameEdge: "#2e3340",
  panel: "#13151c",
  panelLine: "#1f232d",
  knob: "#5a5f6e",
  body: "#cbc4b4",
  eye: "#0b0c10",
};

let bgCache = null;
const buildBackground = () => {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const x = c.getContext("2d");
  x.fillStyle = C.bg;
  x.fillRect(0, 0, W, H);
  x.strokeStyle = C.brick;
  x.lineWidth = 1;
  for (let row = 0, y = 0; y < GROUND; row++, y += 24) {
    x.beginPath();
    x.moveTo(0, y + 0.5);
    x.lineTo(W, y + 0.5);
    x.stroke();
    for (let bx = row % 2 ? 28 : 0; bx < W; bx += 56) {
      x.beginPath();
      x.moveTo(bx + 0.5, y);
      x.lineTo(bx + 0.5, y + 24);
      x.stroke();
    }
  }
  x.fillStyle = "#08090c";
  x.fillRect(0, 0, W, 18);
  return c;
};

const drawLamp = (ctx, tick, dip) => {
  const flicker = 0.85 + Math.sin(tick * 0.07) * 0.05 - (dip ? 0.2 + Math.random() * 0.4 : 0);
  const g = ctx.createRadialGradient(480, 70, 4, 480, 260, 520);
  g.addColorStop(0, `rgba(214,196,150,${0.13 * flicker})`);
  g.addColorStop(1, "rgba(214,196,150,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#20232c";
  ctx.beginPath();
  ctx.moveTo(480.5, 18);
  ctx.lineTo(480.5, 62);
  ctx.stroke();
  ctx.fillStyle = "#16181e";
  ctx.fillRect(476, 62, 9, 7);
  ctx.fillStyle = `rgba(226,210,170,${0.55 * flicker})`;
  ctx.fillRect(476, 62, 9, 7);
};

const drawKey = (ctx, tick) => {
  ctx.save();
  ctx.translate(480.5, 59);
  ctx.rotate(Math.sin(tick * 0.02) * 0.04);
  ctx.globalAlpha = tick % 600 < 5 ? 0.38 : 0.22;
  ctx.strokeStyle = "#9c8d68";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(0, 6, 3, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = "#9c8d68";
  ctx.fillRect(-0.75, 9, 1.5, 10);
  ctx.fillRect(0.5, 15, 2.5, 1.5);
  ctx.fillRect(0.5, 17.5, 2, 1.5);
  ctx.restore();
};

const drawPlatforms = (ctx) => {
  for (const p of PLATFORMS) {
    ctx.fillStyle = C.plat;
    ctx.fillRect(p.x, p.y, p.w, p.h);
    ctx.fillStyle = C.platEdge;
    ctx.fillRect(p.x, p.y, p.w, 2);
  }
};

const interiorFor = (type) => (type === "exit" ? "#d9cfae" : "#050608");

const jit = (seed, n) => ((Math.sin(seed * 91.3 + n * 17.7) * 43758.5453) % 1) * 2.2;

const drawMark = (ctx, x, y, mark, seed) => {
  const j = (n) => jit(seed, n);
  ctx.save();
  ctx.strokeStyle = "rgba(226,221,206,0.8)";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.shadowColor = "rgba(226,221,206,0.35)";
  ctx.shadowBlur = 3;
  ctx.beginPath();
  if (mark === 1) {
    ctx.moveTo(x + j(1), y - 8);
    ctx.lineTo(x + j(2), y + 8);
  } else if (mark === 2) {
    ctx.moveTo(x - 4 + j(1), y - 8);
    ctx.lineTo(x - 4 + j(2), y + 8);
    ctx.moveTo(x + 4 + j(3), y - 8);
    ctx.lineTo(x + 4 + j(4), y + 8);
  } else if (mark === 3) {
    ctx.moveTo(x - 7, y - 7 + j(1));
    ctx.lineTo(x + 7, y + 7 + j(2));
    ctx.moveTo(x + 7, y - 7 + j(3));
    ctx.lineTo(x - 7, y + 7 + j(4));
  } else if (mark === 4) {
    ctx.arc(x + j(1) * 0.5, y, 7, 0.2, Math.PI * 2.05);
  }
  ctx.stroke();
  ctx.restore();
};

const drawDoor = (ctx, d, near, open, type, tick, mark, seed, prompt) => {
  const top = d.y - DOOR_H;
  ctx.fillStyle = C.frame;
  ctx.fillRect(d.x - 5, top - 5, DOOR_W + 10, DOOR_H + 5);
  ctx.fillStyle = C.frameEdge;
  ctx.fillRect(d.x - 5, top - 5, DOOR_W + 10, 2);
  ctx.fillStyle = open > 0 ? interiorFor(type) : "#050608";
  ctx.fillRect(d.x, top, DOOR_W, DOOR_H);
  const pw = DOOR_W * (1 - open);
  if (pw > 0.5) {
    ctx.fillStyle = C.panel;
    ctx.fillRect(d.x, top, pw, DOOR_H);
    ctx.strokeStyle = C.panelLine;
    ctx.strokeRect(d.x + pw * 0.18 + 0.5, top + 8.5, pw * 0.64, 22);
    ctx.strokeRect(d.x + pw * 0.18 + 0.5, top + 38.5, pw * 0.64, 24);
    ctx.fillStyle = C.knob;
    ctx.fillRect(d.x + pw - 9, top + 38, 3, 3);
    if (mark && pw > DOOR_W * 0.6) drawMark(ctx, d.x + pw / 2, top + 20, mark, seed);
  }
  if (near) {
    const a = 0.25 + Math.sin(tick * 0.1) * 0.1;
    ctx.save();
    ctx.shadowColor = "rgba(150,160,205,0.6)";
    ctx.shadowBlur = 12;
    ctx.strokeStyle = `rgba(150,160,205,${a + 0.15})`;
    ctx.strokeRect(d.x - 5.5, top - 5.5, DOOR_W + 11, DOOR_H + 5);
    ctx.restore();
    ctx.fillStyle = `rgba(200,200,215,${a + 0.35})`;
    ctx.font = "10px Silkscreen, monospace";
    ctx.textAlign = "center";
    ctx.fillText(prompt, d.x + DOOR_W / 2, top - 14 + Math.sin(tick * 0.08) * 2);
  }
};

const drawPlayer = (ctx, p, alpha, tick) => {
  const moving = p.onGround && Math.abs(p.vx) > 0.5;
  const bob = moving ? Math.abs(Math.sin(tick * 0.3)) * 2 : 0;
  ctx.save();
  ctx.globalAlpha = alpha * 0.35;
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.ellipse(p.x + p.w / 2, p.groundY ?? p.y + p.h, 12, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = alpha;
  ctx.translate(p.x + p.w / 2, p.y + p.h);
  ctx.scale(1 + p.squash, 1 - p.squash);
  ctx.fillStyle = C.body;
  ctx.fillRect(-p.w / 2, -p.h - bob, p.w, p.h);
  ctx.fillStyle = "#9e978a";
  ctx.fillRect(-p.w / 2, -p.h * 0.42 - bob, p.w, 3);
  ctx.fillStyle = C.eye;
  const ex = p.face > 0 ? 3 : -9;
  ctx.fillRect(ex, -p.h + 9 - bob, 3, 5);
  ctx.fillRect(ex + 6, -p.h + 9 - bob, 3, 5);
  ctx.restore();
};

const drawGassedPlayer = (ctx, g) => {
  const t = g.gas.t;
  const p = g.p;
  const k = Math.min(1, Math.max(0, (t - 58) / 34));
  const wobble = t < 58 ? Math.sin(t * 0.25) * 0.09 * (t / 58) : 0;
  ctx.save();
  ctx.translate(p.x + p.w / 2, p.y + p.h);
  ctx.rotate((k * k * Math.PI) / 2 * p.face + wobble);
  ctx.translate(-(p.x + p.w / 2), -(p.y + p.h));
  drawPlayer(ctx, { ...p, vx: 0, onGround: true, squash: 0 }, 1, g.tick);
  ctx.restore();
};

const drawDust = (ctx, dust) => {
  for (const d of dust) {
    ctx.fillStyle = `rgba(210,200,180,${d.a})`;
    ctx.fillRect(d.x, d.y, d.s, d.s);
  }
};

const drawVignette = (ctx) => {
  const g = ctx.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, W * 0.68);
  g.addColorStop(0, "rgba(5,6,8,0)");
  g.addColorStop(1, "rgba(5,6,8,0.88)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
};

const drawFlash = (ctx, flash) => {
  ctx.fillStyle = flash.color.replace("A", flash.a.toFixed(3));
  ctx.fillRect(0, 0, W, H);
};

export function render(ctx, g) {
  if (g.escape) {
    renderEscape(ctx, g, drawPlayer);
    if (g.flash) drawFlash(ctx, g.flash);
    return;
  }
  if (!bgCache) bgCache = buildBackground();
  ctx.save();
  if (g.shake > 0.3) ctx.translate((Math.random() - 0.5) * g.shake, (Math.random() - 0.5) * g.shake);
  ctx.drawImage(bgCache, 0, 0);
  if (g.room === g.maze.key && !g.hasKey) drawKey(ctx, g.tick);
  drawLamp(ctx, g.tick, g.dipT > 0);
  drawPlatforms(ctx);
  drawVents(ctx, g.puffs.length > 0);
  const near = g.trans ? -1 : g.nearDoor();
  DOORS.forEach((d, i) => {
    const t = g.trans && g.trans.door === i ? g.trans : null;
    const open = !t ? 0 : t.phase === "in" ? Math.min(1, t.t / 10) : t.phase === "end" ? 1 : Math.max(0, 1 - t.t / 16);
    drawDoor(ctx, d, near === i && g.state === "playing", open, t ? t.link.type : null, g.tick, g.marks[`${g.room}-${i}`], g.room * 7 + i, g.prompt);
  });
  if (g.gas) drawGassedPlayer(ctx, g);
  else if (g.playerAlpha > 0) drawPlayer(ctx, g.p, g.playerAlpha, g.tick);
  drawDust(ctx, g.dust);
  drawPuffs(ctx, g.puffs);
  if (g.gas) {
    ctx.fillStyle = `rgba(52,62,40,${Math.min(0.72, g.gas.t / 170)})`;
    ctx.fillRect(0, 0, W, H);
  }
  drawVignette(ctx);
  ctx.restore();
  if (g.fade > 0) {
    ctx.fillStyle = `rgba(3,3,5,${g.fade})`;
    ctx.fillRect(0, 0, W, H);
  }
  if (g.flash) drawFlash(ctx, g.flash);
}
